/**
 * Angebotscache lesend: Suche, Filter, seitenweises Nachladen. Filter stehen in der
 * URL, damit eine Ansicht teilbar ist und „Zurück“ funktioniert. Einzige Aktion: eine bei
 * eBay fremd geänderte Beschreibung ausdrücklich überschreiben. Seit 31.10.2026 (Flori) wieder sichtbar, auch in der
 * BETA; Überschreiben und Live-Vorschau bleiben eingefroren bzw. in der BETA gesperrt (API `editable: false`).
 */
import { Alert, Anchor, Badge, Button, Group, Image, Modal, Select, Stack, Table, Text, TextInput, Title, Tooltip } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ITEM_BACKUPS_SHOWN, type DescriptionBackupView, type ItemView } from '../../../shared/contract.js';
import { ApiRequestError, type ItemsParams } from '../api/client.js';
import { dateTime, has, money, t, type TextKey } from '../texts.js';
import { useServices } from '../services.js';
import { EBAY_SITES, siteLabel } from '../sites.js';
import { warningText } from './render-preview.js';
import { ItemPreviewButton } from './item-preview.js';
import { LiveBlockPreviewButton } from './live-block-preview.js';
import { FullPageLoader, QueryErrorState } from './tenant-layout.js';

export interface ItemsSearch {
  q?: string | undefined;
  status?: 'active' | 'ended' | 'all' | undefined;
  site?: number | undefined;
  listingType?: 'auction' | 'fixed' | 'store' | 'other' | undefined;
  assigned?: 'yes' | 'no' | undefined;
}

const STATUS = ['active', 'ended', 'all'] as const;
const TYPES = ['auction', 'fixed', 'store', 'other'] as const;

/** Prüft die Suchparameter aus der URL; Unbekanntes fällt still weg. */
/**
 * Jeder Schlüssel wird ausdrücklich gesetzt, ungültige als `undefined`: der Router legt die Rohwerte der Elternroute
 * darunter, ein weggelassener Schlüssel käme sonst roh in der Seite an (`?q=110591435961` liest er als Zahl).
 */
export function parseItemsSearch(raw: Record<string, unknown>): ItemsSearch {
  const rawQ = typeof raw['q'] === 'number' && Number.isSafeInteger(raw['q']) && raw['q'] >= 0 ? String(raw['q']) : raw['q'];
  const site = Number(raw['site']);
  return {
    q: typeof rawQ === 'string' && rawQ.trim() ? rawQ.trim().slice(0, 100) : undefined,
    status: STATUS.includes(raw['status'] as never) ? (raw['status'] as ItemsSearch['status']) : undefined,
    site: raw['site'] !== undefined && raw['site'] !== '' && Number.isInteger(site) && site >= 0 && site <= 999 ? site : undefined,
    listingType: TYPES.includes(raw['listingType'] as never) ? (raw['listingType'] as ItemsSearch['listingType']) : undefined,
    assigned: raw['assigned'] === 'yes' || raw['assigned'] === 'no' ? raw['assigned'] : undefined,
  };
}

export function toItemsParams(search: ItemsSearch): ItemsParams {
  return {
    status: search.status ?? 'active',
    ...(search.q ? { q: search.q } : {}),
    ...(search.site !== undefined ? { site: search.site } : {}),
    ...(search.listingType ? { listingType: search.listingType } : {}),
    ...(search.assigned ? { assigned: search.assigned === 'yes' ? 'true' : 'false' } : {}),
  };
}

const EMBED_COLORS: Record<NonNullable<ItemView['embedState']>, string> = {
  unknown: 'gray',
  'in-sync': 'green',
  pending: 'blue',
  revising: 'blue',
  failed: 'red',
  blocked: 'yellow',
  diverged: 'orange',
};

/** Noch verkaufbare Menge, wenn eBay beides liefert; sonst die Gesamtmenge. */
function available(item: ItemView): number | undefined {
  if (item.quantity === undefined) return undefined;
  return item.quantitySold === undefined ? item.quantity : Math.max(0, item.quantity - item.quantitySold);
}

/**
 * „Diese Beschreibung trotzdem überschreiben“ (Festlegung Flori, 24.10.2026): je Angebot, nur nach Bestätigung
 * und nur für den angezeigten Stand (`divergedAt`). Die API merkt den Auftrag vor, der Worker führt ihn aus.
 */
function backupReason(reason: string): string {
  const key = `backups.reason.${reason}`;
  return t(has(key) ? key : ('backups.reason.other' satisfies TextKey));
}

/** Sicherungen der vorherigen Beschreibung (nur Metadaten, neueste zuerst); geladen erst beim Öffnen. */
function BackupsAction({ tenantId, item }: { tenantId: string; item: ItemView }) {
  const { api } = useServices();
  const [open, setOpen] = useState(false);
  const query = useQuery({ queryKey: ['item-backups', tenantId, item.ebayItemId], queryFn: () => api.itemBackups(tenantId, item.ebayItemId), enabled: open });
  const rows = (backups: DescriptionBackupView[]) =>
    backups.map(b => (
      <Table.Tr key={`${b.capturedAt}-${b.bytes}`}>
        <Table.Td>{dateTime(b.capturedAt)}</Table.Td>
        <Table.Td ta="right">{t('backups.size', { kb: Math.max(1, Math.ceil(b.bytes / 1024)) })}</Table.Td>
        <Table.Td>{backupReason(b.reason)}</Table.Td>
        <Table.Td>{t(`backups.mode.${b.mode}`)}</Table.Td>
      </Table.Tr>
    ));
  return (
    <>
      <Button size="compact-xs" variant="subtle" onClick={() => setOpen(true)} aria-label={`${t('backups.open')}: ${item.ebayItemId}`}>
        {t('backups.open')}
      </Button>
      <Modal opened={open} onClose={() => setOpen(false)} title={t('backups.title')} size="lg">
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            {t('backups.intro')}
          </Text>
          {query.isPending ? (
            <Text size="sm">{t('common.loading')}</Text>
          ) : query.isError ? (
            <Alert color="red" role="alert">
              {query.error instanceof ApiRequestError && query.error.detail ? query.error.detail : t('common.error')}
            </Alert>
          ) : query.data.backups.length === 0 ? (
            <Text size="sm">{t('backups.none')}</Text>
          ) : (
            <>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('backups.col.at')}</Table.Th>
                    <Table.Th ta="right">{t('backups.col.size')}</Table.Th>
                    <Table.Th>{t('backups.col.reason')}</Table.Th>
                    <Table.Th>{t('backups.col.mode')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>{rows(query.data.backups)}</Table.Tbody>
              </Table>
              {query.data.total > query.data.backups.length && (
                <Text size="xs" c="dimmed">
                  {t('backups.more', { shown: Math.min(ITEM_BACKUPS_SHOWN, query.data.backups.length), total: query.data.total })}
                </Text>
              )}
            </>
          )}
        </Stack>
      </Modal>
    </>
  );
}

function OverwriteAction({ tenantId, item, divergedAt }: { tenantId: string; item: ItemView; divergedAt: string }) {
  const { api } = useServices();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const overwrite = useMutation({
    mutationFn: () => api.overwriteDescription(tenantId, item.ebayItemId, divergedAt),
    onSuccess: () => {
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['items', tenantId] });
    },
    // Veralteter Stand: Liste neu laden, damit die aktuelle Abweichung sichtbar wird.
    onError: error => {
      if (error instanceof ApiRequestError && error.status === 409) void queryClient.invalidateQueries({ queryKey: ['items', tenantId] });
    },
  });
  // Bis der Worker den Zustand ändert, bleibt das Angebot `diverged`; ein zweiter Auftrag wäre überflüssig.
  if (overwrite.isSuccess) {
    return (
      <Text size="xs" c="green">
        {t('items.overwrite.done')}
      </Text>
    );
  }
  const close = () => {
    setOpen(false);
    overwrite.reset();
  };
  return (
    <>
      <Button size="compact-xs" variant="light" color="orange" onClick={() => setOpen(true)}>
        {t('items.overwrite.open')}
      </Button>
      <Modal opened={open} onClose={close} title={t('items.overwrite.title')}>
        <Stack gap="sm">
          <Text size="sm" fw={500}>
            {item.title}
          </Text>
          <Text size="sm">{t('items.overwrite.text', { date: dateTime(divergedAt) })}</Text>
          <Alert color="yellow" variant="light">
            {t('items.overwrite.warning')}
          </Alert>
          {overwrite.isError && (
            <Alert color="red" role="alert">
              {overwrite.error instanceof ApiRequestError && overwrite.error.detail ? overwrite.error.detail : t('items.overwrite.failed')}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={close}>
              {t('common.cancel')}
            </Button>
            <Button color="orange" onClick={() => overwrite.mutate()} loading={overwrite.isPending}>
              {t('items.overwrite.confirm')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

function ItemRow({ tenantId, item, editable }: { tenantId: string; item: ItemView; editable: boolean }) {
  return (
    <Table.Tr>
      <Table.Td>
        <Group gap="sm" wrap="nowrap">
          {item.pictureUrl ? (
            <Image src={item.pictureUrl} alt="" w={48} h={48} fit="contain" referrerPolicy="no-referrer" />
          ) : (
            <div style={{ width: 48, height: 48 }} />
          )}
          <Stack gap={2}>
            <Text size="sm" fw={500} lineClamp={2}>
              {item.title}
            </Text>
            <Group gap={6}>
              <Text size="xs" c="dimmed">
                {item.ebayUrl ? (
                  <Anchor href={item.ebayUrl} target="_blank" rel="noopener noreferrer" size="xs" title={t('items.openOnEbay')}>
                    {item.ebayItemId}
                  </Anchor>
                ) : (
                  item.ebayItemId
                )}
                {' · '}
                {siteLabel(item.site)}
                {item.sku ? ` · ${item.sku}` : ''}
              </Text>
              {item.patSearch && (
                <Text size="xs" c="dimmed" lineClamp={1} title={item.patSearch}>
                  {t('items.patSearch')}: {item.patSearch}
                </Text>
              )}
              {item.hasVariations && (
                <Badge size="xs" variant="outline">
                  {t('items.variations')}
                </Badge>
              )}
              {item.status !== 'active' && (
                <Badge size="xs" color={item.status === 'soldout' ? 'orange' : 'gray'}>
                  {t(`items.state.${item.status}`)}
                </Badge>
              )}
            </Group>
          </Stack>
        </Group>
      </Table.Td>
      <Table.Td>{t(`items.type.${item.listingType}`)}</Table.Td>
      <Table.Td ta="right">{money(item.price.amount, item.price.currency)}</Table.Td>
      <Table.Td ta="right">{available(item) ?? t('common.unknown')}</Table.Td>
      <Table.Td>{dateTime(item.endTime)}</Table.Td>
      <Table.Td>
        <Group gap={4}>
          {item.articleId ? <Badge variant="light">{item.articleId}</Badge> : t('common.unknown')}
          {item.embedState && item.embedState !== 'in-sync' && (
            <Badge size="xs" variant="outline" color={EMBED_COLORS[item.embedState]}>
              {t(`embed.${item.embedState}`)}
            </Badge>
          )}
          {item.embedWarnings && item.embedWarnings.length > 0 && (
            // Beschreibung wurde geschrieben, aber mit Einschränkungen (z. B. leere Galeriebox); kein Fehler.
            <Tooltip label={[...new Set(item.embedWarnings.map(w => warningText(w.split(':')[0] ?? w)))].join(' · ')} multiline w={280}>
              <Badge size="xs" variant="light" color="gray">
                {t('embed.warnings', { n: String(item.embedWarnings.length) })}
              </Badge>
            </Tooltip>
          )}
          {editable && item.status === 'active' && ((item.orderOutcome?.outcome === 'article-changed' && item.articleId) || item.orderOutcome?.outcome === 'source-missing') && (
            // Ausgang des letzten Auftrags je Angebot (core `DescriptionOrderOutcome`); beauftragt wird beim Artikel.
            <Badge size="xs" variant="light" color="orange">
              {t(`embed.error.${item.orderOutcome.outcome}.items`)}
            </Badge>
          )}
          {item.writeUnresolved && (
            // Ungeklärter früherer Schreibvorgang (OpenWrite): Ausgang bei eBay unbekannt, neue Schreibaufträge gesperrt.
            <Tooltip label={t('items.writeUnresolvedSince', { date: dateTime(item.writeUnresolved.since) })} multiline w={280}>
              <Badge size="xs" variant="light" color="orange">
                {t('items.writeUnresolved')}
              </Badge>
            </Tooltip>
          )}
          {/* Sicherungen gibt es nur, wo PAT die Beschreibung kennt (EMBED#). */}
          {item.embedState && <BackupsAction tenantId={tenantId} item={item} />}
          {item.articleId && <ItemPreviewButton tenantId={tenantId} ebayItemId={item.ebayItemId} />}
          {editable && item.embedMode === 'block' && item.status === 'active' && <LiveBlockPreviewButton tenantId={tenantId} ebayItemId={item.ebayItemId} />}
          {editable && item.embedState === 'diverged' && item.divergedAt && item.status !== 'ended' && !item.writeUnresolved && (
            <OverwriteAction tenantId={tenantId} item={item} divergedAt={item.divergedAt} />
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

export function ItemsPage({ tenantId, search }: { tenantId: string; search: ItemsSearch }) {
  const { api } = useServices();
  const navigate = useNavigate();
  const [text, setText] = useState(search.q ?? '');
  const [debounced] = useDebouncedValue(text, 350);

  const update = (patch: Partial<ItemsSearch>) =>
    void navigate({
      to: '/t/$tenantId/items',
      params: { tenantId },
      search: prev => parseItemsSearch({ ...prev, ...patch } as Record<string, unknown>),
      replace: true,
    });

  useEffect(() => {
    if ((debounced.trim() || undefined) !== search.q) update({ q: debounced.trim() || undefined } as Partial<ItemsSearch>);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur auf die entprellte Eingabe reagieren
  }, [debounced]);

  const params = toItemsParams(search);
  const query = useInfiniteQuery({
    queryKey: ['items', tenantId, params],
    queryFn: ({ pageParam }) => api.items(tenantId, { ...params, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: last => last.nextCursor,
  });

  const items = query.data?.pages.flatMap(page => page.items) ?? [];
  const order = query.data?.pages[0]?.order;
  const editable = query.data?.pages[0]?.editable ?? false;

  return (
    <Stack gap="md">
      <Title order={2}>{t('items.title')}</Title>
      <Group align="end" gap="sm">
        <TextInput
          label={t('items.search')}
          placeholder={t('items.search')}
          value={text}
          onChange={e => setText(e.currentTarget.value)}
          w={320}
          maxLength={100}
        />
        <Select
          label={t('items.status')}
          w={140}
          allowDeselect={false}
          value={search.status ?? 'active'}
          data={STATUS.map(value => ({ value, label: t(`items.status.${value}`) }))}
          onChange={value => update({ status: (value ?? 'active') as ItemsSearch['status'] & string })}
        />
        <Select
          label={t('items.site')}
          w={170}
          value={search.site !== undefined ? String(search.site) : 'all'}
          allowDeselect={false}
          data={[{ value: 'all', label: t('items.site.all') }, ...EBAY_SITES.map(site => ({ value: String(site.id), label: site.label }))]}
          onChange={value => update({ site: value && value !== 'all' ? Number(value) : undefined } as Partial<ItemsSearch>)}
        />
        <Select
          label={t('items.type')}
          w={150}
          value={search.listingType ?? 'all'}
          allowDeselect={false}
          data={[{ value: 'all', label: t('items.type.all') }, ...TYPES.map(value => ({ value, label: t(`items.type.${value}`) }))]}
          onChange={value => update({ listingType: value && value !== 'all' ? value : undefined } as Partial<ItemsSearch>)}
        />
        <Select
          label={t('items.assigned')}
          w={170}
          value={search.assigned ?? 'all'}
          allowDeselect={false}
          data={[
            { value: 'all', label: t('items.assigned.all') },
            { value: 'yes', label: t('items.assigned.yes') },
            { value: 'no', label: t('items.assigned.no') },
          ]}
          onChange={value => update({ assigned: value === 'yes' || value === 'no' ? value : undefined } as Partial<ItemsSearch>)}
        />
      </Group>
      {order && (
        <Text size="xs" c="dimmed">
          {t(`items.order.${order}`)}
        </Text>
      )}
      {query.isPending && <FullPageLoader />}
      {query.isError && <QueryErrorState error={query.error} onRetry={() => void query.refetch()} />}
      {query.data && items.length === 0 && <Text c="dimmed">{t('items.empty')}</Text>}
      {items.length > 0 && (
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t('items.col.item')}</Table.Th>
              <Table.Th>{t('items.type')}</Table.Th>
              <Table.Th ta="right">{t('items.col.price')}</Table.Th>
              <Table.Th ta="right">{t('items.col.quantity')}</Table.Th>
              <Table.Th>{t('items.col.end')}</Table.Th>
              <Table.Th>{t('items.col.article')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map(item => (
              <ItemRow key={item.ebayItemId} tenantId={tenantId} item={item} editable={editable} />
            ))}
          </Table.Tbody>
        </Table>
      )}
      {query.hasNextPage && (
        <Group justify="center">
          <Button variant="light" loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
            {t('items.more')}
          </Button>
        </Group>
      )}
    </Stack>
  );
}
